package com.evoapps.evoshift.ui.workplaces

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Map
import androidx.compose.material.icons.filled.Place
import androidx.compose.material.icons.filled.TravelExplore
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.model.AddressSearchFailure
import com.evoapps.evoshift.domain.model.AddressSuggestion
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.ui.map.MapViewState
import com.evoapps.evoshift.ui.map.OSM_COPYRIGHT_URL
import com.evoapps.evoshift.ui.map.OsmMap
import com.evoapps.evoshift.viewmodels.WorkplaceLocationKey
import com.evoapps.evoshift.viewmodels.WorkplaceLocationViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import com.evoapps.evoshift.widgets.EvoSwitchRow
import com.evoapps.evoshift.widgets.EvoTextField
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.settings_address
import evoshift.composeapp.generated.resources.settings_address_no_matches
import evoshift.composeapp.generated.resources.settings_address_not_found
import evoshift.composeapp.generated.resources.settings_address_search_busy
import evoshift.composeapp.generated.resources.settings_address_search_failed
import evoshift.composeapp.generated.resources.settings_address_search_help
import evoshift.composeapp.generated.resources.settings_address_searching
import evoshift.composeapp.generated.resources.settings_coordinates_by_hand
import evoshift.composeapp.generated.resources.settings_coordinates_found
import evoshift.composeapp.generated.resources.settings_edit_location
import evoshift.composeapp.generated.resources.settings_find_coordinates
import evoshift.composeapp.generated.resources.settings_geocode_failed
import evoshift.composeapp.generated.resources.settings_geoplace
import evoshift.composeapp.generated.resources.settings_geoplace_help
import evoshift.composeapp.generated.resources.settings_geoplace_hint
import evoshift.composeapp.generated.resources.settings_geoplace_invalid
import evoshift.composeapp.generated.resources.settings_map_hint
import evoshift.composeapp.generated.resources.settings_map_no_pin
import evoshift.composeapp.generated.resources.settings_show_on_map
import org.jetbrains.compose.resources.stringResource

/**
 * The workplace's address and its point (SH-110, SH-132). Typing the address suggests places
 * from OpenStreetMap (through our server); picking one fills the address and moves the pin.
 * "Find from address" asks the phone's own geocoder first (Android, iOS), then OpenStreetMap. The
 * map (OpenStreetMap tiles) shows the pin; a click or tap moves it. The coordinates field is the
 * advanced way in, kept in step with the pin. Saving is refused while the coordinates are not
 * two numbers in range; empty clears them. The stored shape (`workplaces/{id}.location`) is
 * unchanged.
 */
@Composable
fun WorkplaceLocationDialog(
    workplaceId: String,
    market: Market,
    initialAddress: String,
    initialLocation: GeoLocation?,
    /** Bumped by the screen at each opening: a fresh dialog, not the one cancelled before. */
    opening: Int,
    onDismiss: () -> Unit,
    onSave: (address: String, location: GeoLocation?) -> Unit
) {
    val viewModel = diViewModel<WorkplaceLocationKey, WorkplaceLocationViewModel>(
        WorkplaceLocationKey(workplaceId, initialAddress, initialLocation, opening)
    )
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val geocoder = rememberAddressGeocoder()
    // Read here, outside the dialog window: inside it Android provides its own handler.
    val uriHandler = LocalUriHandler.current
    var byHand by remember { mutableStateOf(false) }
    val mapState = remember {
        val (center, zoom) = initialLocation?.let { it to WorkplaceLocationViewModel.POINT_ZOOM } ?: countryView(market)
        MapViewState(center, zoom)
    }
    LaunchedEffect(state.mapFocus) { state.mapFocus?.let { mapState.moveTo(it.location, it.zoom) } }

    val location = state.location
    val invalid = state.coordinatesInvalid
    val showField = byHand || invalid

    EvoDialog(
        title = stringResource(Res.string.settings_edit_location),
        icon = Icons.Default.Place,
        onDismiss = onDismiss,
        confirm = DialogAction(
            stringResource(Res.string.save),
            { if (!invalid) onSave(state.address.trim(), location) },
            enabled = !invalid && !state.isFinding
        )
    ) {
        EvoTextField(
            value = state.address,
            onValueChange = viewModel::onAddressChange,
            label = stringResource(Res.string.settings_address),
            supportingText = stringResource(Res.string.settings_address_search_help)
        )
        AddressSearchStatus(state.isSuggesting, state.noMatches, state.searchFailure)
        if (state.suggestions.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                state.suggestions.forEach { place -> SuggestionRow(place) { viewModel.pickSuggestion(place) } }
            }
        }
        EvoSecondaryButton(
            DialogAction(
                stringResource(Res.string.settings_find_coordinates),
                { viewModel.findCoordinates(geocoder) },
                enabled = state.address.isNotBlank(),
                loading = state.isFinding,
                icon = Icons.Default.TravelExplore
            ),
            Modifier.fillMaxWidth()
        )
        state.lookup?.let { result ->
            Text(
                stringResource(
                    when (result) {
                        is GeocodeResult.Found -> Res.string.settings_coordinates_found
                        GeocodeResult.NotFound -> Res.string.settings_address_not_found
                        GeocodeResult.Failed -> Res.string.settings_geocode_failed
                    }
                ),
                style = MaterialTheme.typography.bodySmall,
                color = if (result is GeocodeResult.Found) Color.White.copy(alpha = 0.8f) else ErrorOnNavy
            )
        }

        OsmMap(
            state = mapState,
            pin = location,
            onPick = viewModel::placePin,
            onAttribution = { uriHandler.openUri(OSM_COPYRIGHT_URL) },
            modifier = Modifier.fillMaxWidth().height(260.dp)
        )
        Text(
            stringResource(Res.string.settings_map_hint),
            style = MaterialTheme.typography.bodySmall,
            color = Color.White.copy(alpha = 0.7f)
        )

        EvoSwitchRow(
            checked = showField,
            onCheckedChange = { byHand = it },
            label = stringResource(Res.string.settings_coordinates_by_hand),
            supporting = if (showField) null else location?.format() ?: stringResource(Res.string.settings_map_no_pin)
        )
        if (showField) {
            EvoTextField(
                value = state.coordinates,
                onValueChange = viewModel::onCoordinatesChange,
                label = stringResource(Res.string.settings_geoplace),
                placeholder = stringResource(Res.string.settings_geoplace_hint),
                supportingText = stringResource(Res.string.settings_geoplace_help),
                error = stringResource(Res.string.settings_geoplace_invalid).takeIf { invalid },
                keyboardType = KeyboardType.Ascii,
                imeAction = ImeAction.Done
            )
        }
        val link = mapLink(location, state.address)
        EvoSecondaryButton(
            DialogAction(
                stringResource(Res.string.settings_show_on_map),
                { link?.let(uriHandler::openUri) },
                enabled = link != null,
                icon = Icons.Default.Map
            ),
            Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun AddressSearchStatus(searching: Boolean, noMatches: Boolean, failure: AddressSearchFailure?) {
    val text = when {
        searching -> Res.string.settings_address_searching
        failure == AddressSearchFailure.BUSY -> Res.string.settings_address_search_busy
        failure != null -> Res.string.settings_address_search_failed
        noMatches -> Res.string.settings_address_no_matches
        else -> return
    }
    Text(
        stringResource(text),
        style = MaterialTheme.typography.bodySmall,
        color = if (failure != null) ErrorOnNavy else Color.White.copy(alpha = 0.7f)
    )
}

@Composable
private fun SuggestionRow(place: AddressSuggestion, onPick: () -> Unit) {
    Surface(
        color = Color.White.copy(alpha = 0.08f),
        shape = RoundedCornerShape(10.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onPick)
    ) {
        Row(Modifier.padding(horizontal = 12.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.Place, contentDescription = null, tint = Color.White.copy(alpha = 0.7f), modifier = Modifier.size(18.dp))
            Text(
                place.label,
                style = MaterialTheme.typography.bodyMedium,
                color = Color.White,
                modifier = Modifier.padding(start = 10.dp)
            )
        }
    }
}

/** The whole country of [market] when there is no point yet. */
private fun countryView(market: Market): Pair<GeoLocation, Int> = when (market) {
    Market.POLAND -> GeoLocation(52.07, 19.48) to 6
}
